import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Icon } from './uploadUi';
import { formatBytes } from './uploadFormat';
import type { IconName } from './icons';
import { severityOf, type Severity, type UploadError } from './uploadTypes';

/**
 * Why an upload did not produce a khutbah — in the register the outcome
 * deserves.
 *
 * This component is the reason `UploadError` is a typed union rather than a
 * message. Three outcomes arrive through the same failure path and must not
 * look alike:
 *
 * - **Wrong format** cannot proceed. Destructive border, and the only way
 *   forward is a different file.
 * - **Wrong text encoding** is *fixable in a minute*. Warning, not
 *   destructive, and it leads with what to do — shouting at an editor whose
 *   khutbah is perfectly fine would be both wrong and discouraging.
 * - **Already uploaded** is not a failure at all. A plain row and a link to
 *   the khutbah it was linked to.
 *
 * Severity is derived from `kind` in one place (`severityOf`), so the three
 * cannot drift apart as copy changes.
 */

const FRAMES: Record<Severity, string> = {
  destructive: 'border-destructive',
  warning: 'border-warning',
  info: 'border-border',
};

const ICON_TONES: Record<Severity, string> = {
  destructive: 'text-destructive',
  warning: 'text-warning',
  info: 'text-muted-foreground',
};

const ICONS: Record<Severity, IconName> = {
  destructive: 'alert',
  warning: 'alert',
  info: 'info',
};

/** Title, body, and the optional how-to line that only `encoding` carries. */
function wording(error: UploadError): {
  title: string;
  body: string;
  how?: string;
  icon?: IconName;
} {
  switch (error.kind) {
    case 'format':
      return {
        title: 'This file is not a PDF, a Word document or plain text',
        body:
          "The format is read from the file's own bytes, not its name, so renaming " +
          'it will not help. Export the khutbah as PDF, .docx or UTF-8 text and ' +
          'upload that.',
      };
    case 'encoding':
      return {
        title: 'This text file is not UTF-8',
        body:
          'If it is Arabic saved in a legacy encoding such as Windows-1256, convert ' +
          'it to UTF-8 and upload again. Nothing about the khutbah itself is wrong.',
        how:
          'In most editors: Save as… → Encoding → UTF-8. Word: Save as plain text → ' +
          'Other encoding → Unicode (UTF-8).',
      };
    case 'size':
      return {
        title: 'This file is over the size limit',
        body: `It is ${formatBytes(error.bytes)}, and the limit is ${formatBytes(
          error.limitBytes,
        )}. A khutbah is a few pages of text — a file this large is usually scanned images, which need a different route.`,
      };
    case 'pages':
      return {
        title: 'This document has too many pages',
        body: `It has ${error.pages} pages, and the limit is ${error.limitPages}. Check that this is one Friday's khutbah and not a collection.`,
      };
    case 'same-friday':
      return {
        title: 'This publisher already has a khutbah for this Friday',
        body: error.published
          ? 'That one is published and readers can see it, so it cannot be replaced by an upload. Unpublish it first, or choose a different Friday.'
          : 'Uploading this as a second khutbah would leave readers with two for one Friday. If this file is a corrected version, replace the existing one; if the date is wrong, change the Friday above.',
        icon: 'calendar',
      };
    case 'published':
      return {
        title: 'That khutbah is published',
        body:
          'Swapping the text underneath a reader is the one thing a published ' +
          'khutbah must never do. Unpublish it first, then upload again.',
        icon: 'alert',
      };
    case 'duplicate':
      return {
        title: 'This file was uploaded before',
        body:
          'Its content matches an existing khutbah, so it was linked to that one ' +
          'rather than creating a second. Nothing new was generated.',
        icon: 'link',
      };
    case 'auth':
      return {
        title: 'Sign in to the editor surface first',
        body:
          'Uploading is behind a password. Open an editor address directly so the ' +
          'browser asks for the credentials, then come back and choose the file ' +
          'again — a background upload cannot raise that prompt by itself.',
        how:
          'Authentication is still a placeholder here (brief §18): one shared ' +
          'editor account over HTTP Basic, not the real mechanism.',
        icon: 'alert',
      };
    case 'network':
      return {
        title: 'The upload did not reach the server',
        body: error.message,
      };
  }
}

export function UploadNotice({
  error,
  onChooseAnother,
  onRetry,
  onReplace,
}: {
  error: UploadError;
  onChooseAnother: () => void;
  /** Absent unless repeating the request unchanged could plausibly succeed. */
  onRetry?: (() => void) | undefined;
  /**
   * Supersede the khutbah already occupying this Friday. Offered only when
   * the server said the existing one is not published — this is the
   * permission step, so it is never taken without a press.
   */
  onReplace?: (() => void) | undefined;
}) {
  const severity = severityOf(error);
  const { title, body, how, icon } = wording(error);

  return (
    <div
      // Destructive and warning interrupt what the editor was doing, so they
      // are announced assertively. A duplicate is news, not a problem, and
      // `status` lets a screen reader finish the sentence it was on.
      role={severity === 'info' ? 'status' : 'alert'}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-3.5',
        FRAMES[severity],
        severity === 'warning' && 'bg-warning-bg',
      )}
    >
      <Icon
        name={icon ?? ICONS[severity]}
        size={18}
        className={cn('mt-0.5', ICON_TONES[severity])}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="font-semibold">{title}</p>
        <p
          className={cn(
            'text-[13px] leading-[18px] text-pretty',
            // A fixable problem is read in full; a destructive one only needs
            // to be scanned on the way to choosing another file.
            severity === 'warning' ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {body}
        </p>

        {how ? <p className="text-xs leading-4 text-muted-foreground">{how}</p> : null}

        {'khutbahId' in error && error.khutbahId ? (
          <p className="text-[13px] leading-[18px]">
            <a
              href={`?khutbah=${encodeURIComponent(error.khutbahId)}`}
              className="underline underline-offset-2"
            >
              {error.kind === 'duplicate'
                ? 'Open the khutbah it was linked to'
                : 'Open the khutbah already there'}
            </a>
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {/* Destructive, and styled as such even inside a warning frame: the
              existing khutbah's passages and any translations go with it. */}
          {error.kind === 'same-friday' && !error.published && onReplace ? (
            <Button type="button" size="lg" variant="destructive" onClick={onReplace}>
              Replace the existing khutbah
            </Button>
          ) : null}
          {onRetry ? (
            <Button type="button" size="lg" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button
            type="button"
            size="lg"
            variant={severity === 'destructive' ? 'outline' : 'ghost'}
            onClick={onChooseAnother}
          >
            {error.kind === 'encoding'
              ? 'Upload the converted file'
              : error.kind === 'same-friday' || error.kind === 'published'
                ? 'Cancel this upload'
                : 'Choose another file'}
          </Button>
        </div>
      </div>
    </div>
  );
}
